import { useEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";
import { TgScreen } from "../components/TgScreen";
import { Card } from "../components/atoms";
import { useShieldedKeys } from "../shielded/keys-context";
import { buildZip321Uri } from "../lib/zip321";
import { truncateAddr } from "../lib/format";
import { haptic } from "../lib/haptics";

// Telegram's Mini App WebApp surface exposes `shareURL` but our local
// type stub doesn't include it yet. Narrow the access through a
// purpose-built type so we don't have to widen the global declaration
// (which lives outside our edit scope).
type ShareCapableWebApp = {
  shareURL?: (url: string, text?: string) => void;
};

export function ReceiveScreen() {
  const { keys, state } = useShieldedKeys();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);
  const [amount, setAmount] = useState("");
  const [memo, setMemo] = useState("");

  // Parse the amount input into a finite positive number (or undefined).
  // Empty string and any non-numeric / negative / zero values are
  // treated as "no amount requested" so we silently fall back to a
  // bare `zcash:<address>` URI rather than embedding garbage.
  const amountZec = useMemo(() => {
    const trimmed = amount.trim();
    if (!trimmed) return undefined;
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n <= 0) return undefined;
    return n;
  }, [amount]);

  const memoStr = memo.trim() || undefined;

  // The QR encodes either the bare UA (default) or a full ZIP-321 URI
  // when the user has filled in amount/memo. Senders running ZIP-321
  // wallets get a pre-filled compose screen; senders that don't read
  // ZIP-321 still see the address (everything after `zcash:` until the
  // `?` is parseable as a UA).
  const payloadUri = useMemo(() => {
    if (!keys) return "";
    return buildZip321Uri({
      address: keys.unifiedAddress,
      amount_zec: amountZec,
      memo: memoStr,
    });
  }, [keys, amountZec, memoStr]);

  // Render the QR on first paint + whenever the payload changes.
  // Unified Addresses are long — ZIP-316 with Sapling + Orchard
  // receivers is ~180 chars, which sits in QR version ~17. Use
  // medium error correction so a phone-photo capture still decodes
  // with a finger-shadow over a corner.
  useEffect(() => {
    if (!keys || !canvasRef.current || !payloadUri) return;
    QRCode.toCanvas(canvasRef.current, payloadUri, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 240,
      color: {
        dark: "#000000",
        light: "#ffffff",
      },
    }).catch((e: unknown) => {
      // eslint-disable-next-line no-console
      console.warn("[receive] QR render failed", e);
    });
  }, [keys, payloadUri]);

  const copy = async () => {
    if (!payloadUri) return;
    try {
      await navigator.clipboard.writeText(payloadUri);
      setCopied(true);
      haptic("success");
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // fall back: select-and-copy is browser default
    }
  };

  const share = async () => {
    if (!payloadUri) return;
    // Preferred: Telegram's native share sheet. Falls through to the
    // Web Share API on regular browsers, and finally to plain copy if
    // neither is available.
    const wa = window.Telegram?.WebApp as ShareCapableWebApp | undefined;
    if (wa?.shareURL) {
      try {
        wa.shareURL(payloadUri, "Send me ZEC");
        setShared(true);
        haptic("success");
        window.setTimeout(() => setShared(false), 2000);
        return;
      } catch {
        // fall through to next strategy
      }
    }
    const nav = navigator as Navigator & {
      share?: (data: { text?: string; url?: string }) => Promise<void>;
    };
    if (typeof nav.share === "function") {
      try {
        await nav.share({ text: payloadUri });
        setShared(true);
        haptic("success");
        window.setTimeout(() => setShared(false), 2000);
        return;
      } catch {
        // user dismissed or no share targets — fall through to copy
      }
    }
    // Fallback to copy — copy() already fires haptic("success").
    void copy();
  };

  return (
    <TgScreen title="Receive" bottomNav="receive">
      <div style={{ padding: "12px 16px 24px" }}>
        <Card style={{ padding: 18 }}>
          <div
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: 1,
              color: "var(--hint)",
              marginBottom: 12,
            }}
          >
            Your unified address
          </div>
          {keys ? (
            <>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  marginBottom: 14,
                }}
              >
                <label
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--hint)",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Request amount (ZEC, optional)
                  </span>
                  <input
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    inputMode="decimal"
                    placeholder="0.0"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    style={{
                      padding: "8px 10px",
                      background: "var(--bg-2, rgba(255,255,255,0.04))",
                      border:
                        "1px solid var(--hairline, rgba(255,255,255,0.08))",
                      borderRadius: 8,
                      outline: "none",
                      fontSize: 14,
                      fontFamily: "var(--font-text)",
                      color: "var(--text)",
                    }}
                  />
                </label>
                <label
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--hint)",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Memo (optional)
                  </span>
                  <input
                    value={memo}
                    onChange={(e) => setMemo(e.target.value)}
                    placeholder="e.g. Invoice #1234"
                    style={{
                      padding: "8px 10px",
                      background: "var(--bg-2, rgba(255,255,255,0.04))",
                      border:
                        "1px solid var(--hairline, rgba(255,255,255,0.08))",
                      borderRadius: 8,
                      outline: "none",
                      fontSize: 14,
                      fontFamily: "var(--font-text)",
                      color: "var(--text)",
                    }}
                  />
                </label>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    padding: 8,
                    background: "white",
                    borderRadius: 8,
                  }}
                >
                  <canvas ref={canvasRef} aria-label="Unified Address QR code" />
                </div>
              </div>
              {(amountZec != null || memoStr) && (
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--hint)",
                    textAlign: "center",
                    lineHeight: 1.4,
                    marginBottom: 12,
                  }}
                >
                  Senders with ZIP-321 wallets will see the amount and memo
                  pre-filled.
                </div>
              )}
              <div
                style={{
                  fontSize: 12,
                  fontFamily: "monospace",
                  color: "var(--text-soft)",
                  marginBottom: 4,
                }}
                title={keys.unifiedAddress}
              >
                {truncateAddr(keys.unifiedAddress)}
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  marginTop: 14,
                }}
              >
                <button
                  onClick={copy}
                  style={{
                    flex: 1,
                    padding: "10px 14px",
                    background: copied
                      ? "rgba(46, 187, 96, 0.18)"
                      : "var(--tg-theme-button-color, #2c8acb)",
                    color: copied
                      ? "var(--text)"
                      : "var(--tg-theme-button-text-color, white)",
                    border: "none",
                    borderRadius: 10,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "background 0.15s",
                  }}
                >
                  {copied ? "✓ Copied" : "Copy"}
                </button>
                <button
                  onClick={() => void share()}
                  style={{
                    flex: 1,
                    padding: "10px 14px",
                    background: shared
                      ? "rgba(46, 187, 96, 0.18)"
                      : "var(--bg-2, rgba(255,255,255,0.06))",
                    color: shared ? "var(--success, #34C759)" : "var(--text)",
                    border:
                      "1px solid var(--hairline, rgba(255,255,255,0.08))",
                    borderRadius: 10,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "var(--font-text)",
                    transition: "background 0.15s",
                  }}
                >
                  {shared ? "✓ Shared" : "Share"}
                </button>
              </div>
            </>
          ) : state.kind === "deriving" ? (
            <div style={{ fontSize: 13, color: "var(--hint)" }}>
              Deriving shielded keys…
            </div>
          ) : state.kind === "error" ? (
            <div style={{ fontSize: 13, color: "var(--destructive, #d33)" }}>
              {state.message}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: "var(--hint)" }}>
              Wallet not unlocked.
            </div>
          )}
        </Card>

        <div
          style={{
            marginTop: 16,
            fontSize: 12,
            color: "var(--hint)",
            lineHeight: 1.5,
          }}
        >
          Senders can scan the QR or paste this u1… address into any
          Zcash wallet. Shieldz users can also find you via
          @username in the Contacts tab.
        </div>
      </div>
    </TgScreen>
  );
}
